import { useCallback, useMemo, useState } from 'react';
import { useAI } from './useAI';

/** Quick capture shares chat's durable bridge, with its own recovery scope. */
export function useTaskCapture(scope: 'quick-inbox' | 'process-inbox') {
  const [input, setInput] = useState('');
  const { sendMessage, messages, isLoading, progress, error, clearMessages } = useAI({ captureScope: scope });

  const submit = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    const message = text.includes(',') ? `Create these tasks: ${text}` : `Create a task: ${text}`;
    const response = await sendMessage(message);
    // Keep the draft after failure or recovery; either may include saved edits.
    if (response) setInput(current => current.trim() === text ? '' : current);
  }, [input, isLoading, sendMessage]);

  const receipt = useMemo(() => {
    const created = messages.flatMap(message => message.tool_results || [])
      .filter(call => call.tool === 'create_task' && call.success && call.result.success !== false && !call.result.error);
    if (!created.length) return null;
    const titles = created.map(call => {
      const task = call.result.task as { title?: unknown } | undefined;
      return typeof task?.title === 'string' ? `“${task.title}”` : 'a task';
    });
    return `Created ${titles.join(' and ')}`;
  }, [messages]);

  const reply = [...messages].reverse().find(message => message.role === 'assistant' && message.content)?.content;
  return { input, setInput, submit, isLoading, progress, error, receipt, dismiss: clearMessages,
    reply: !receipt && !error ? reply : undefined };
}
